/**
 * /vendors/:vendorId/qualification — Vendor Qualification detail.
 *
 * Sections:
 *   - Status header: badge, last reviewed, action buttons (Submit / Approve / Reject / etc.)
 *   - Required-cert checklist (missing = red)
 *   - Certificate vault (COI cards with expiry countdown)
 *   - Licenses
 *   - Bonds
 *   - Documents (misc uploads with required-type checklist)
 *   - Audit event timeline
 *   - Right column: editable cards — Tax/W-9, Safety, Diversity, Financial
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import VerifiedIcon from "@mui/icons-material/Verified";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { FileDropZone } from "@/components/FileDropZone";
import { MoneyField } from "@/components/MoneyField";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SettingsIcon from "@mui/icons-material/Settings";

import { api } from "@/lib/api";
import { vendorQualQueryOptions } from "@/lib/queries";
import type {
  VendorCertificate,
  VendorDocument,
  VendorDocumentType,
  VendorLicense,
  VendorBond,
  VendorQualStatus,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/vendors/$vendorId/qualification")({
  component: VendorQualificationPage,
});

// ─── helpers ────────────────────────────────────────────────────────────────

const STATUS_COLOR: Record<VendorQualStatus, "default" | "warning" | "success" | "error" | "info"> = {
  pending: "default",
  under_review: "info",
  approved: "success",
  conditional: "warning",
  expired: "error",
  disqualified: "error",
};

const STATUS_LABEL: Record<VendorQualStatus, string> = {
  pending: "Pending",
  under_review: "Under Review",
  approved: "Approved",
  conditional: "Conditional",
  expired: "Expired",
  disqualified: "Disqualified",
};

const CERT_LABELS: Record<string, string> = {
  general_liability: "General Liability",
  workers_comp: "Workers' Comp",
  auto_liability: "Auto Liability",
  professional_liability: "Professional Liability",
  umbrella: "Umbrella",
  builders_risk: "Builder's Risk",
  pollution: "Pollution Liability",
};

function fmtDate(s: string | null | undefined) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtCurrency(n: number | null | undefined) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function fmtBytes(n: number | null | undefined) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((new Date(dateStr).getTime() - today.getTime()) / 86_400_000);
}

function ExpiryBadge({ dateStr }: { dateStr: string }) {
  const days = daysUntil(dateStr);
  if (days < 0) return <span className="text-xs text-red-600 font-medium">Expired {Math.abs(days)}d ago</span>;
  if (days <= 30) return <span className="text-xs text-red-500 font-medium">Expires in {days}d</span>;
  if (days <= 90) return <span className="text-xs text-amber-600 font-medium">Expires in {days}d</span>;
  return <span className="text-xs text-slate-500 dark:text-slate-400">{fmtDate(dateStr)}</span>;
}

// ─── AI doc upload dialog ─────────────────────────────────────────────────────

const DOC_TYPE_LABELS: Record<string, string> = {
  coi_general_liability: "COI — General Liability",
  coi_workers_comp:      "COI — Workers Comp",
  coi_auto_liability:    "COI — Auto Liability",
  w9:                    "W-9",
  contractor_license:    "Contractor License",
  lien_waiver:           "Lien Waiver",
};

interface IngestResult {
  envelope_id: string | null;
  doc_type: string;
  final_confidence: number;
  subject_name: string | null;
  status: string;
  error: string | null;
  extracted: Record<string, unknown>;
}

interface AiUploadDialogProps {
  open: boolean;
  partyId: string;
  onClose: () => void;
  onDone: () => void;
}

function AiUploadDialog({ open, partyId, onClose, onDone }: AiUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [hintType, setHintType] = useState<string>("");
  const [result, setResult] = useState<IngestResult | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setFile(null);
    setHintType("");
    setResult(null);
    setError(null);
    setUploading(false);
  }

  async function handleSubmit() {
    if (!file) return;
    setUploading(true);
    setError(null);
    setResult(null);
    const form = new FormData();
    form.append("file", file);
    if (hintType) form.append("hint_doc_type", hintType);
    form.append("hint_party_id", partyId);
    try {
      const token = localStorage.getItem("token") ?? "";
      const resp = await fetch("/api/agents/doc-ingest", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail ?? resp.statusText);
      setResult(data as IngestResult);
      if (data.status === "auto_committed") onDone();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  }

  const handleClose = () => { reset(); onClose(); };
  const statusColor = result?.status === "auto_committed"
    ? "bg-emerald-50 dark:bg-emerald-950 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300"
    : result?.status === "queued_review"
    ? "bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300"
    : "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className="flex items-center gap-2">
        <AutoAwesomeIcon sx={{ fontSize: 18, color: "#6366f1" }} />
        AI Document Upload
      </DialogTitle>
      <DialogContent className="flex flex-col gap-4 pt-2">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Upload a COI, W-9, contractor license, or lien waiver. The AI will classify, extract all
          fields, and automatically file it to this vendor's record if confidence is high enough.
        </p>
        <FileDropZone
          file={file}
          onFile={setFile}
          onClear={() => setFile(null)}
          accept=".pdf,image/jpeg,image/png,image/webp"
          label="Drop or click to select PDF or image"
          sublabel="PDF · JPG · PNG · WEBP"
        />
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Document type hint (optional)</label>
          <select value={hintType} onChange={e => setHintType(e.target.value)} className="border border-slate-300 rounded px-2 py-1.5 text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-slate-100">
            <option value="">— Let AI classify —</option>
            {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {result && (
          <div className={`rounded-lg border p-3 text-sm ${statusColor}`}>
            <div className="font-semibold mb-1">
              {result.status === "auto_committed" && "✓ Filed automatically"}
              {result.status === "queued_review" && "⏳ Queued for review"}
              {result.status === "unknown" && "⚠ Couldn't classify document"}
              {result.status === "error" && "✗ Error"}
            </div>
            <div className="text-xs space-y-0.5">
              <div>Type: <span className="font-mono">{DOC_TYPE_LABELS[result.doc_type] ?? result.doc_type}</span></div>
              <div>Confidence: {Math.round(result.final_confidence * 100)}%</div>
              {result.subject_name && <div>Vendor: {result.subject_name}</div>}
              {result.error && <div>Error: {result.error}</div>}
            </div>
          </div>
        )}
        {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      </DialogContent>
      <DialogActions>
        <button type="button" onClick={handleClose} className="text-sm px-3 py-1.5 text-slate-600 dark:text-slate-300">{result ? "Close" : "Cancel"}</button>
        {!result && (
          <button type="button" disabled={!file || uploading} onClick={handleSubmit}
            className="text-sm px-4 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
            {uploading ? <><CircularProgress size={12} sx={{ color: "white" }} /> Processing…</> : <><AutoAwesomeIcon sx={{ fontSize: 14 }} /> Upload &amp; Ingest</>}
          </button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ─── cert add dialog ─────────────────────────────────────────────────────────

interface AddCertDialogProps {
  open: boolean;
  qualId: string;
  partyId: string;
  onClose: () => void;
}

function AddCertDialog({ open, qualId: _qualId, partyId, onClose }: AddCertDialogProps) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ cert_type: "general_liability", carrier_name: "", policy_number: "", expiry_date: "", coverage_aggregate: "" });
  const mut = useMutation({
    mutationFn: () => api(`/api/vendors/${partyId}/qualification/certificates`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...form, coverage_aggregate: form.coverage_aggregate ? Number(form.coverage_aggregate) : null }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor-qual", partyId] });
      onClose();
      setForm({ cert_type: "general_liability", carrier_name: "", policy_number: "", expiry_date: "", coverage_aggregate: "" });
    },
  });
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Certificate</DialogTitle>
      <DialogContent className="flex flex-col gap-3 pt-2">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Cert Type</label>
          <select value={form.cert_type} onChange={e => setForm(f => ({ ...f, cert_type: e.target.value }))} className="border border-slate-300 rounded px-2 py-1.5 text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-slate-100">
            {Object.entries(CERT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <TextField size="small" label="Carrier name" value={form.carrier_name} onChange={e => setForm(f => ({ ...f, carrier_name: e.target.value }))} fullWidth required />
        <TextField size="small" label="Policy number" value={form.policy_number} onChange={e => setForm(f => ({ ...f, policy_number: e.target.value }))} fullWidth />
        <TextField size="small" label="Expiry date" type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} fullWidth required InputLabelProps={{ shrink: true }} />
        <MoneyField size="small" label="Coverage aggregate" value={form.coverage_aggregate} onChange={(v) => setForm(f => ({ ...f, coverage_aggregate: v }))} fullWidth />
        {mut.isError && <p className="text-xs text-red-600">{(mut.error as Error).message}</p>}
      </DialogContent>
      <DialogActions>
        <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 text-slate-600 hover:text-slate-900 dark:text-slate-300">Cancel</button>
        <button type="button" disabled={!form.carrier_name || !form.expiry_date || mut.isPending} onClick={() => mut.mutate()} className="text-sm px-4 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
          {mut.isPending ? "Saving…" : "Add Certificate"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

// ─── add license dialog ───────────────────────────────────────────────────────

function AddLicenseDialog({ open, partyId, onClose }: { open: boolean; partyId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ license_type: "contractor", license_number: "", issuing_jurisdiction: "", expiry_date: "" });
  const mut = useMutation({
    mutationFn: () => api(`/api/vendors/${partyId}/qualification/licenses`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vendor-qual", partyId] }); onClose(); },
  });
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add License</DialogTitle>
      <DialogContent className="flex flex-col gap-3 pt-2">
        <TextField size="small" label="License type" value={form.license_type} onChange={e => setForm(f => ({ ...f, license_type: e.target.value }))} fullWidth />
        <TextField size="small" label="License number" value={form.license_number} onChange={e => setForm(f => ({ ...f, license_number: e.target.value }))} fullWidth required />
        <TextField size="small" label="Issuing jurisdiction" value={form.issuing_jurisdiction} onChange={e => setForm(f => ({ ...f, issuing_jurisdiction: e.target.value }))} fullWidth required />
        <TextField size="small" label="Expiry date" type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} fullWidth required InputLabelProps={{ shrink: true }} />
        {mut.isError && <p className="text-xs text-red-600">{(mut.error as Error).message}</p>}
      </DialogContent>
      <DialogActions>
        <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 text-slate-600 dark:text-slate-300">Cancel</button>
        <button type="button" disabled={!form.license_number || !form.issuing_jurisdiction || !form.expiry_date || mut.isPending} onClick={() => mut.mutate()} className="text-sm px-4 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
          {mut.isPending ? "Saving…" : "Add License"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

// ─── cert card ───────────────────────────────────────────────────────────────

function CertCard({ cert, partyId }: { cert: VendorCertificate; partyId: string }) {
  const qc = useQueryClient();
  const verifyMut = useMutation({
    mutationFn: () => api(`/api/vendors/${partyId}/qualification/certificates/${cert.id}/verify`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vendor-qual", partyId] }),
  });
  const days = daysUntil(cert.expiry_date);
  const isExpired = days < 0;
  const borderColor = isExpired ? "border-red-300 dark:border-red-800" : days <= 30 ? "border-amber-300 dark:border-amber-800" : "border-slate-200 dark:border-gray-700";
  return (
    <div className={`rounded-lg border ${borderColor} bg-white dark:bg-gray-900 p-3 flex flex-col gap-1.5`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{CERT_LABELS[cert.cert_type] ?? cert.cert_type}</span>
        <div className="flex items-center gap-2">
          {cert.verified_at ? (
            <Tooltip title={`Verified ${fmtDate(cert.verified_at)}`}>
              <VerifiedIcon sx={{ fontSize: 16, color: "#22c55e" }} />
            </Tooltip>
          ) : (
            <button type="button" onClick={() => verifyMut.mutate()} disabled={verifyMut.isPending}
              className="text-[10px] px-2 py-0.5 rounded border border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-950 disabled:opacity-50">
              Verify
            </button>
          )}
        </div>
      </div>
      <div className="text-xs text-slate-500 dark:text-slate-400">{cert.carrier_name}</div>
      {cert.policy_number && <div className="text-xs text-slate-400 dark:text-slate-500 font-mono">{cert.policy_number}</div>}
      <div className="flex items-center justify-between mt-1">
        <div className="flex gap-2 text-xs">
          {cert.additional_insured && <span className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">AI</span>}
          {cert.waiver_of_subrogation && <span className="bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded">WOS</span>}
        </div>
        <ExpiryBadge dateStr={cert.expiry_date} />
      </div>
    </div>
  );
}

// ─── status action buttons ────────────────────────────────────────────────────

function StatusActions({ status, partyId }: { status: VendorQualStatus; partyId: string }) {
  const qc = useQueryClient();
  const [rejectReason, setRejectReason] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);

  const transition = (endpoint: string, body?: object) =>
    api(`/api/vendors/${partyId}/qualification/${endpoint}`, {
      method: "POST",
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });

  const submitMut = useMutation({ mutationFn: () => transition("submit-review"), onSuccess: () => qc.invalidateQueries({ queryKey: ["vendor-qual", partyId] }) });
  const approveMut = useMutation({ mutationFn: () => transition("approve"), onSuccess: () => qc.invalidateQueries({ queryKey: ["vendor-qual", partyId] }) });
  const rejectMut = useMutation({
    mutationFn: () => transition("reject", { reason: rejectReason }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vendor-qual", partyId] }); setRejectOpen(false); },
  });
  const disqualMut = useMutation({ mutationFn: () => transition("disqualify", { reason: "Manual disqualification" }), onSuccess: () => qc.invalidateQueries({ queryKey: ["vendor-qual", partyId] }) });

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {(status === "pending" || status === "conditional") && (
        <button type="button" disabled={submitMut.isPending} onClick={() => submitMut.mutate()}
          className="text-sm px-3 py-1.5 rounded-md border border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-950 disabled:opacity-50">
          {submitMut.isPending ? "…" : "Submit for Review"}
        </button>
      )}
      {status === "under_review" && (
        <>
          <button type="button" disabled={approveMut.isPending} onClick={() => approveMut.mutate()}
            className="text-sm px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
            {approveMut.isPending ? "…" : "Approve"}
          </button>
          <button type="button" onClick={() => setRejectOpen(true)}
            className="text-sm px-3 py-1.5 rounded-md border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950">
            Reject
          </button>
        </>
      )}
      {(status === "approved" || status === "conditional") && (
        <button type="button" disabled={disqualMut.isPending} onClick={() => disqualMut.mutate()}
          className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-gray-700 dark:text-slate-300 dark:hover:bg-gray-800 disabled:opacity-50">
          Disqualify
        </button>
      )}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject vendor qualification</DialogTitle>
        <DialogContent className="pt-2">
          <TextField size="small" label="Reason" value={rejectReason} onChange={e => setRejectReason(e.target.value)} fullWidth multiline rows={3} />
        </DialogContent>
        <DialogActions>
          <button type="button" onClick={() => setRejectOpen(false)} className="text-sm px-3 py-1.5 text-slate-600 dark:text-slate-300">Cancel</button>
          <button type="button" disabled={rejectMut.isPending} onClick={() => rejectMut.mutate()}
            className="text-sm px-4 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            {rejectMut.isPending ? "…" : "Reject"}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// ─── required certs checklist ────────────────────────────────────────────────

const REQUIRED_BY_CLASS: Record<string, string[]> = {
  subcontractor: ["general_liability", "workers_comp", "auto_liability"],
  supplier_material: ["general_liability"],
  supplier_equipment: ["general_liability"],
  consultant: ["general_liability", "professional_liability"],
  professional: ["general_liability", "professional_liability"],
  labor_only: ["workers_comp"],
};

function RequiredCertChecklist({ certs, classification }: { certs: VendorCertificate[]; classification: string | null }) {
  const required = REQUIRED_BY_CLASS[classification ?? ""] ?? [];
  if (required.length === 0) return null;
  const activeCertTypes = new Set(certs.filter(c => c.is_active && daysUntil(c.expiry_date) >= 0).map(c => c.cert_type));
  return (
    <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Required Certificates</h3>
      <ul className="space-y-1.5">
        {required.map(ct => {
          const ok = activeCertTypes.has(ct);
          return (
            <li key={ct} className="flex items-center gap-2 text-sm">
              {ok ? <CheckCircleIcon sx={{ fontSize: 16, color: "#22c55e" }} /> : <ErrorOutlineIcon sx={{ fontSize: 16, color: "#ef4444" }} />}
              <span className={ok ? "text-slate-700 dark:text-slate-300" : "text-red-600 dark:text-red-400"}>{CERT_LABELS[ct] ?? ct}</span>
              {!ok && <span className="text-xs text-red-500 dark:text-red-400">missing</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─── edit dialogs for right-column cards ─────────────────────────────────────

interface EditCardDialogProps {
  open: boolean;
  onClose: () => void;
  partyId: string;
  title: string;
  children: React.ReactNode;
  onSave: () => void;
  isPending: boolean;
  isError: boolean;
  errorMsg?: string;
}

function EditCardDialog({ open, onClose, title, children, onSave, isPending, isError, errorMsg }: EditCardDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent className="flex flex-col gap-3 pt-2">
        {children}
        {isError && <p className="text-xs text-red-600 dark:text-red-400">{errorMsg}</p>}
      </DialogContent>
      <DialogActions>
        <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 text-slate-600 dark:text-slate-300">Cancel</button>
        <button type="button" disabled={isPending} onClick={onSave} className="text-sm px-4 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
          {isPending ? "Saving…" : "Save"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Tax/W-9 edit dialog ─────────────────────────────────────────────────────

function EditTaxDialog({ open, partyId, initial, onClose }: {
  open: boolean; partyId: string;
  initial: { ein: string; business_type: string; is_1099_eligible: boolean; w9_on_file: boolean; w9_signed_date: string; payment_terms_default: string };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState(initial);
  const mut = useMutation({
    mutationFn: () => api(`/api/vendors/${partyId}/qualification`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ein: form.ein || null,
        business_type: form.business_type || null,
        is_1099_eligible: form.is_1099_eligible,
        w9_on_file: form.w9_on_file,
        w9_signed_date: form.w9_signed_date || null,
        payment_terms_default: form.payment_terms_default || null,
      }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vendor-qual", partyId] }); onClose(); },
  });
  // Re-sync when dialog opens
  const handleOpen = () => setForm(initial);

  return (
    <EditCardDialog open={open} onClose={onClose} title="Edit Tax / W-9" partyId={partyId} onSave={() => mut.mutate()} isPending={mut.isPending} isError={mut.isError} errorMsg={(mut.error as Error | null)?.message}>
      <TextField size="small" label="EIN" value={form.ein} onChange={e => setForm(f => ({ ...f, ein: e.target.value }))} fullWidth placeholder="12-3456789" />
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Business type</label>
        <select value={form.business_type} onChange={e => setForm(f => ({ ...f, business_type: e.target.value }))} className="border border-slate-300 rounded px-2 py-1.5 text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-slate-100">
          <option value="">— Select —</option>
          <option value="LLC">LLC</option>
          <option value="Corporation">Corporation</option>
          <option value="S-Corp">S-Corp</option>
          <option value="Sole Proprietor">Sole Proprietor</option>
          <option value="Partnership">Partnership</option>
          <option value="Other">Other</option>
        </select>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
        <input type="checkbox" checked={form.is_1099_eligible} onChange={e => setForm(f => ({ ...f, is_1099_eligible: e.target.checked }))} className="w-4 h-4" />
        1099 eligible
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
        <input type="checkbox" checked={form.w9_on_file} onChange={e => setForm(f => ({ ...f, w9_on_file: e.target.checked }))} className="w-4 h-4" />
        W-9 on file
      </label>
      <TextField size="small" label="W-9 signed date" type="date" value={form.w9_signed_date} onChange={e => setForm(f => ({ ...f, w9_signed_date: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
      <TextField size="small" label="Payment terms (e.g. Net 30)" value={form.payment_terms_default} onChange={e => setForm(f => ({ ...f, payment_terms_default: e.target.value }))} fullWidth />
    </EditCardDialog>
  );
}

// ─── Safety edit dialog ───────────────────────────────────────────────────────

function EditSafetyDialog({ open, partyId, initial, onClose }: {
  open: boolean; partyId: string;
  initial: { emr: string; emr_year: string; trir: string; trir_year: string; osha_300_on_file: boolean; drug_testing_policy: boolean; union_local: string; union_craft: string; prevailing_wage_compliant: boolean };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState(initial);
  const mut = useMutation({
    mutationFn: () => api(`/api/vendors/${partyId}/qualification`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        emr: form.emr ? parseFloat(form.emr) : null,
        emr_year: form.emr_year ? parseInt(form.emr_year) : null,
        trir: form.trir ? parseFloat(form.trir) : null,
        trir_year: form.trir_year ? parseInt(form.trir_year) : null,
        osha_300_on_file: form.osha_300_on_file,
        drug_testing_policy: form.drug_testing_policy,
        union_local: form.union_local || null,
        union_craft: form.union_craft || null,
        prevailing_wage_compliant: form.prevailing_wage_compliant,
      }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vendor-qual", partyId] }); onClose(); },
  });
  return (
    <EditCardDialog open={open} onClose={onClose} title="Edit Safety" partyId={partyId} onSave={() => mut.mutate()} isPending={mut.isPending} isError={mut.isError} errorMsg={(mut.error as Error | null)?.message}>
      <div className="grid grid-cols-2 gap-3">
        <TextField size="small" label="EMR" type="number" inputProps={{ step: "0.01" }} value={form.emr} onChange={e => setForm(f => ({ ...f, emr: e.target.value }))} fullWidth />
        <TextField size="small" label="EMR year" type="number" value={form.emr_year} onChange={e => setForm(f => ({ ...f, emr_year: e.target.value }))} fullWidth />
        <TextField size="small" label="TRIR" type="number" inputProps={{ step: "0.001" }} value={form.trir} onChange={e => setForm(f => ({ ...f, trir: e.target.value }))} fullWidth />
        <TextField size="small" label="TRIR year" type="number" value={form.trir_year} onChange={e => setForm(f => ({ ...f, trir_year: e.target.value }))} fullWidth />
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
        <input type="checkbox" checked={form.osha_300_on_file} onChange={e => setForm(f => ({ ...f, osha_300_on_file: e.target.checked }))} className="w-4 h-4" />
        OSHA 300 log on file
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
        <input type="checkbox" checked={form.drug_testing_policy} onChange={e => setForm(f => ({ ...f, drug_testing_policy: e.target.checked }))} className="w-4 h-4" />
        Drug testing policy in place
      </label>
      <TextField size="small" label="Union local" value={form.union_local} onChange={e => setForm(f => ({ ...f, union_local: e.target.value }))} fullWidth />
      <TextField size="small" label="Union craft" value={form.union_craft} onChange={e => setForm(f => ({ ...f, union_craft: e.target.value }))} fullWidth />
      <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
        <input type="checkbox" checked={form.prevailing_wage_compliant} onChange={e => setForm(f => ({ ...f, prevailing_wage_compliant: e.target.checked }))} className="w-4 h-4" />
        Prevailing wage compliant
      </label>
    </EditCardDialog>
  );
}

// ─── Diversity edit dialog ────────────────────────────────────────────────────

function EditDiversityDialog({ open, partyId, initial, onClose }: {
  open: boolean; partyId: string;
  initial: { is_mbe: boolean; mbe_cert_expiry: string; is_wbe: boolean; wbe_cert_expiry: string; is_dbe: boolean; dbe_cert_expiry: string; is_vosb: boolean };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState(initial);
  const mut = useMutation({
    mutationFn: () => api(`/api/vendors/${partyId}/qualification`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        is_mbe: form.is_mbe,
        mbe_cert_expiry: form.mbe_cert_expiry || null,
        is_wbe: form.is_wbe,
        wbe_cert_expiry: form.wbe_cert_expiry || null,
        is_dbe: form.is_dbe,
        dbe_cert_expiry: form.dbe_cert_expiry || null,
        is_vosb: form.is_vosb,
      }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vendor-qual", partyId] }); onClose(); },
  });
  return (
    <EditCardDialog open={open} onClose={onClose} title="Edit Diversity Certifications" partyId={partyId} onSave={() => mut.mutate()} isPending={mut.isPending} isError={mut.isError} errorMsg={(mut.error as Error | null)?.message}>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer w-24">
            <input type="checkbox" checked={form.is_mbe} onChange={e => setForm(f => ({ ...f, is_mbe: e.target.checked }))} className="w-4 h-4" /> MBE
          </label>
          {form.is_mbe && <TextField size="small" label="MBE cert expiry" type="date" value={form.mbe_cert_expiry} onChange={e => setForm(f => ({ ...f, mbe_cert_expiry: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer w-24">
            <input type="checkbox" checked={form.is_wbe} onChange={e => setForm(f => ({ ...f, is_wbe: e.target.checked }))} className="w-4 h-4" /> WBE
          </label>
          {form.is_wbe && <TextField size="small" label="WBE cert expiry" type="date" value={form.wbe_cert_expiry} onChange={e => setForm(f => ({ ...f, wbe_cert_expiry: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer w-24">
            <input type="checkbox" checked={form.is_dbe} onChange={e => setForm(f => ({ ...f, is_dbe: e.target.checked }))} className="w-4 h-4" /> DBE
          </label>
          {form.is_dbe && <TextField size="small" label="DBE cert expiry" type="date" value={form.dbe_cert_expiry} onChange={e => setForm(f => ({ ...f, dbe_cert_expiry: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />}
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
          <input type="checkbox" checked={form.is_vosb} onChange={e => setForm(f => ({ ...f, is_vosb: e.target.checked }))} className="w-4 h-4" /> VOSB
        </label>
      </div>
    </EditCardDialog>
  );
}

// ─── Financial edit dialog ────────────────────────────────────────────────────

function EditFinancialDialog({ open, partyId, initial, onClose }: {
  open: boolean; partyId: string;
  initial: { credit_limit: string; bond_capacity_single: string; bond_capacity_aggregate: string };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState(initial);
  const mut = useMutation({
    mutationFn: () => api(`/api/vendors/${partyId}/qualification`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        credit_limit: form.credit_limit ? parseFloat(form.credit_limit) : null,
        bond_capacity_single: form.bond_capacity_single ? parseFloat(form.bond_capacity_single) : null,
        bond_capacity_aggregate: form.bond_capacity_aggregate ? parseFloat(form.bond_capacity_aggregate) : null,
      }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vendor-qual", partyId] }); onClose(); },
  });
  return (
    <EditCardDialog open={open} onClose={onClose} title="Edit Financial" partyId={partyId} onSave={() => mut.mutate()} isPending={mut.isPending} isError={mut.isError} errorMsg={(mut.error as Error | null)?.message}>
      <MoneyField size="small" label="Credit limit" value={form.credit_limit} onChange={(v) => setForm(f => ({ ...f, credit_limit: v }))} fullWidth />
      <MoneyField size="small" label="Bond capacity — single job" value={form.bond_capacity_single} onChange={(v) => setForm(f => ({ ...f, bond_capacity_single: v }))} fullWidth />
      <MoneyField size="small" label="Bond capacity — aggregate" value={form.bond_capacity_aggregate} onChange={(v) => setForm(f => ({ ...f, bond_capacity_aggregate: v }))} fullWidth />
    </EditCardDialog>
  );
}

// ─── Manage doc types dialog ──────────────────────────────────────────────────

function ManageDocTypesDialog({ open, onClose, partyId: _partyId }: { open: boolean; onClose: () => void; partyId: string }) {
  const qc = useQueryClient();
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newRequired, setNewRequired] = useState(false);

  const typesQ = useQuery({
    queryKey: ["vendor-doc-types"],
    queryFn: () => api<VendorDocumentType[]>("/api/vendors/document-types?include_inactive=true"),
    enabled: open,
  });

  const createMut = useMutation({
    mutationFn: () => api("/api/vendors/document-types", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newName, description: newDesc || null, is_required: newRequired }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vendor-doc-types"] }); setNewName(""); setNewDesc(""); setNewRequired(false); },
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      api(`/api/vendors/document-types/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ is_active }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vendor-doc-types"] }),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Document Types</DialogTitle>
      <DialogContent className="flex flex-col gap-4 pt-2">
        {/* Existing types */}
        {typesQ.isLoading && <CircularProgress size={20} />}
        {typesQ.data && typesQ.data.length > 0 && (
          <div className="divide-y divide-slate-100 dark:divide-gray-800">
            {typesQ.data.map(dt => (
              <div key={dt.id} className="flex items-center justify-between py-2">
                <div>
                  <span className={`text-sm font-medium ${dt.is_active ? "text-slate-700 dark:text-slate-300" : "text-slate-400 dark:text-slate-500 line-through"}`}>{dt.name}</span>
                  {dt.is_required && <span className="ml-2 text-[10px] bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 px-1.5 py-0.5 rounded">Required</span>}
                  {dt.description && <p className="text-xs text-slate-400 dark:text-slate-500">{dt.description}</p>}
                </div>
                <button type="button"
                  onClick={() => toggleMut.mutate({ id: dt.id, is_active: !dt.is_active })}
                  className="text-xs px-2 py-0.5 rounded border border-slate-300 dark:border-gray-700 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-gray-800">
                  {dt.is_active ? "Deactivate" : "Activate"}
                </button>
              </div>
            ))}
          </div>
        )}
        {typesQ.data?.length === 0 && <p className="text-sm text-slate-400 dark:text-slate-500">No document types defined yet.</p>}

        {/* Add new type */}
        <div className="border-t border-slate-100 dark:border-gray-800 pt-3 flex flex-col gap-2">
          <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">Add new type</p>
          <TextField size="small" label="Name" value={newName} onChange={e => setNewName(e.target.value)} fullWidth required />
          <TextField size="small" label="Description (optional)" value={newDesc} onChange={e => setNewDesc(e.target.value)} fullWidth />
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
            <input type="checkbox" checked={newRequired} onChange={e => setNewRequired(e.target.checked)} className="w-4 h-4" />
            Required for all vendors
          </label>
          {createMut.isError && <p className="text-xs text-red-600">{(createMut.error as Error).message}</p>}
          <button type="button" disabled={!newName || createMut.isPending} onClick={() => createMut.mutate()}
            className="self-start text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            {createMut.isPending ? "Adding…" : "Add type"}
          </button>
        </div>
      </DialogContent>
      <DialogActions>
        <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 text-slate-600 dark:text-slate-300">Close</button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Add document dialog ──────────────────────────────────────────────────────

function AddDocumentDialog({ open, partyId, onClose }: { open: boolean; partyId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [docTypeId, setDocTypeId] = useState<string>("");
  const [customName, setCustomName] = useState("");
  const [notes, setNotes] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const typesQ = useQuery({
    queryKey: ["vendor-doc-types"],
    queryFn: () => api<VendorDocumentType[]>("/api/vendors/document-types"),
    enabled: open,
  });

  function reset() {
    setFile(null); setDocTypeId(""); setCustomName(""); setNotes(""); setExpiryDate(""); setError(null); setUploading(false);
  }

  async function handleSubmit() {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { setError("File must be under 20MB"); return; }
    setUploading(true);
    setError(null);
    const form = new FormData();
    form.append("file", file);
    if (docTypeId && docTypeId !== "__adhoc__") form.append("doc_type_id", docTypeId);
    if (customName) form.append("custom_name", customName);
    if (notes) form.append("notes", notes);
    if (expiryDate) form.append("expiry_date", expiryDate);
    try {
      const token = localStorage.getItem("token") ?? "";
      const resp = await fetch(`/api/vendors/${partyId}/qualification/documents/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail ?? resp.statusText);
      qc.invalidateQueries({ queryKey: ["vendor-qual", partyId] });
      qc.invalidateQueries({ queryKey: ["vendor-docs", partyId] });
      reset();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  }

  const isAdhoc = !docTypeId || docTypeId === "__adhoc__";

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} maxWidth="sm" fullWidth>
      <DialogTitle className="flex items-center gap-2">
        <AttachFileIcon sx={{ fontSize: 18 }} />
        Upload Document
      </DialogTitle>
      <DialogContent className="flex flex-col gap-3 pt-2">
        {/* Doc type */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Document type</label>
          <select value={docTypeId} onChange={e => setDocTypeId(e.target.value)} className="border border-slate-300 rounded px-2 py-1.5 text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-slate-100">
            <option value="__adhoc__">Ad-hoc (no type)</option>
            {typesQ.data?.map(dt => <option key={dt.id} value={dt.id}>{dt.name}{dt.is_required ? " *" : ""}</option>)}
          </select>
        </div>
        {isAdhoc && (
          <TextField size="small" label="Custom name" value={customName} onChange={e => setCustomName(e.target.value)} fullWidth required placeholder="e.g. Safety manual 2025" />
        )}
        {/* File picker */}
        <FileDropZone
          file={file}
          onFile={setFile}
          onClear={() => setFile(null)}
          accept=""
          maxSizeMB={20}
          label="Drop or click to select file"
          sublabel="Any type · max 20 MB"
        />
        <TextField size="small" label="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} fullWidth multiline rows={2} />
        <TextField size="small" label="Expiry date (optional)" type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
        {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      </DialogContent>
      <DialogActions>
        <button type="button" onClick={() => { reset(); onClose(); }} className="text-sm px-3 py-1.5 text-slate-600 dark:text-slate-300">Cancel</button>
        <button type="button" disabled={!file || (isAdhoc && !customName) || uploading} onClick={handleSubmit}
          className="text-sm px-4 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
          {uploading ? <><CircularProgress size={12} sx={{ color: "white" }} /> Uploading…</> : "Upload"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Documents section ────────────────────────────────────────────────────────

function VendorDocumentsSection({ partyId, qualId }: { partyId: string; qualId: string }) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  const docsQ = useQuery({
    queryKey: ["vendor-docs", partyId],
    queryFn: () => api<VendorDocument[]>(`/api/vendors/${partyId}/qualification/documents`),
    staleTime: 15_000,
  });

  const typesQ = useQuery({
    queryKey: ["vendor-doc-types"],
    queryFn: () => api<VendorDocumentType[]>("/api/vendors/document-types"),
    staleTime: 60_000,
  });

  const deleteMut = useMutation({
    mutationFn: (docId: string) => api(`/api/vendors/${partyId}/qualification/documents/${docId}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vendor-docs", partyId] }); qc.invalidateQueries({ queryKey: ["vendor-qual", partyId] }); },
  });

  const docs = docsQ.data ?? [];
  const types = typesQ.data ?? [];
  const requiredTypes = types.filter(t => t.is_required);
  const uploadedTypeIds = new Set(docs.filter(d => d.doc_type_id).map(d => d.doc_type_id!));
  const typeMap = Object.fromEntries(types.map(t => [t.id, t]));

  function getDocLabel(doc: VendorDocument) {
    if (doc.doc_type_id && typeMap[doc.doc_type_id]) return typeMap[doc.doc_type_id].name;
    return doc.custom_name ?? "Untitled";
  }

  function handleDownload(doc: VendorDocument) {
    const token = localStorage.getItem("token") ?? "";
    const url = `/api/vendors/${partyId}/qualification/documents/${doc.id}/file`;
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.file_name ?? "document";
    // We can't set auth header on anchor clicks, so open in new tab with token as query is not ideal.
    // Use fetch instead for authenticated download.
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = doc.file_name ?? "document";
        link.click();
        URL.revokeObjectURL(blobUrl);
      });
  }

  function handleDelete(doc: VendorDocument) {
    if (!window.confirm(`Delete "${getDocLabel(doc)}"? This cannot be undone.`)) return;
    deleteMut.mutate(doc.id);
  }

  return (
    <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Documents</h3>
        <div className="flex items-center gap-2">
          <Tooltip title="Manage document types">
            <IconButton size="small" onClick={() => setManageOpen(true)} sx={{ color: "#94a3b8" }}>
              <SettingsIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <button type="button" onClick={() => setAddOpen(true)}
            className="text-xs px-2.5 py-1 rounded border border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-950">
            + Upload doc
          </button>
        </div>
      </div>

      {/* Required types checklist */}
      {requiredTypes.length > 0 && (
        <div className="mb-3 space-y-1">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase mb-1.5">Required documents</p>
          {requiredTypes.map(rt => {
            const ok = uploadedTypeIds.has(rt.id);
            return (
              <div key={rt.id} className="flex items-center gap-2 text-sm">
                {ok ? <CheckCircleIcon sx={{ fontSize: 15, color: "#22c55e" }} /> : <ErrorOutlineIcon sx={{ fontSize: 15, color: "#ef4444" }} />}
                <span className={ok ? "text-slate-700 dark:text-slate-300" : "text-red-600 dark:text-red-400"}>{rt.name}</span>
                {!ok && <span className="text-xs text-red-500">missing</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* Document list */}
      {docsQ.isLoading && <CircularProgress size={16} />}
      {docs.length === 0 && !docsQ.isLoading && (
        <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-3">No documents uploaded.</p>
      )}
      {docs.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 dark:border-gray-800">
              <th className="text-left py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Name</th>
              <th className="text-left py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase hidden sm:table-cell">File</th>
              <th className="text-left py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase hidden sm:table-cell">Expiry</th>
              <th className="text-left py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Added</th>
              <th className="py-1.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50 dark:divide-gray-800">
            {docs.map(doc => (
              <tr key={doc.id}>
                <td className="py-2 text-slate-700 dark:text-slate-300 max-w-[140px] truncate">{getDocLabel(doc)}</td>
                <td className="py-2 text-slate-500 dark:text-slate-400 text-xs hidden sm:table-cell max-w-[160px] truncate">
                  {doc.file_name ?? "—"}
                  {doc.file_size_bytes && <span className="ml-1 text-slate-400">({fmtBytes(doc.file_size_bytes)})</span>}
                </td>
                <td className="py-2 hidden sm:table-cell">
                  {doc.expiry_date ? <ExpiryBadge dateStr={doc.expiry_date} /> : <span className="text-slate-400 text-xs">—</span>}
                </td>
                <td className="py-2 text-slate-400 dark:text-slate-500 text-xs whitespace-nowrap">{fmtDate(doc.created_at)}</td>
                <td className="py-2">
                  <div className="flex items-center gap-1">
                    <Tooltip title="Download">
                      <IconButton size="small" onClick={() => handleDownload(doc)} sx={{ color: "#64748b" }}>
                        <DownloadIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(doc)} disabled={deleteMut.isPending} sx={{ color: "#94a3b8" }}>
                        <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <AddDocumentDialog open={addOpen} partyId={partyId} onClose={() => setAddOpen(false)} />
      <ManageDocTypesDialog open={manageOpen} partyId={partyId} onClose={() => setManageOpen(false)} />
    </div>
  );
}

// ─── page ────────────────────────────────────────────────────────────────────

function VendorQualificationPage() {
  const { vendorId } = Route.useParams();
  const qualQ = useQuery(vendorQualQueryOptions(vendorId));
  const [addCertOpen, setAddCertOpen] = useState(false);
  const [addLicOpen, setAddLicOpen] = useState(false);
  const [aiUploadOpen, setAiUploadOpen] = useState(false);
  const [editTaxOpen, setEditTaxOpen] = useState(false);
  const [editSafetyOpen, setEditSafetyOpen] = useState(false);
  const [editDiversityOpen, setEditDiversityOpen] = useState(false);
  const [editFinancialOpen, setEditFinancialOpen] = useState(false);
  const qc = useQueryClient();

  const q = qualQ.data;

  return (
    <div className="flex flex-col bg-slate-50 dark:bg-gray-950 overflow-auto">
      {/* sub-header: status + actions */}
      <div className="bg-white border-b border-slate-200 dark:bg-gray-900 dark:border-gray-800 px-6 py-3 flex items-center gap-3">
        <div className="flex items-center gap-2">
          {q && (
            <Chip
              label={STATUS_LABEL[q.qual_status]}
              size="small"
              color={STATUS_COLOR[q.qual_status]}
              variant={q.qual_status === "approved" ? "filled" : "outlined"}
            />
          )}
          {q?.last_reviewed_at && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Last reviewed {fmtDate(q.last_reviewed_at)}
              {q.next_review_due && ` · next due ${fmtDate(q.next_review_due)}`}
            </span>
          )}
          {qualQ.isLoading && <CircularProgress size={14} />}
        </div>
        <div className="flex-1" />
        <button type="button" onClick={() => setAiUploadOpen(true)}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-indigo-300 text-indigo-600 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-400 dark:hover:bg-indigo-950">
          <AutoAwesomeIcon sx={{ fontSize: 14 }} />
          AI Upload
        </button>
        {q && <StatusActions status={q.qual_status} partyId={vendorId} />}
      </div>

      {qualQ.isError && (
        <div className="m-6 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
          {(qualQ.error as Error).message}
        </div>
      )}

      {q && (
        <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: certs + licenses + bonds + docs + audit */}
          <div className="lg:col-span-2 space-y-6">

            {/* Required cert checklist */}
            <RequiredCertChecklist certs={q.certificates} classification={null} />

            {/* Certificates */}
            <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Certificates of Insurance</h3>
                <button type="button" onClick={() => setAddCertOpen(true)}
                  className="text-xs px-2.5 py-1 rounded border border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-950">
                  + Add cert
                </button>
              </div>
              {q.certificates.filter(c => c.is_active).length === 0 ? (
                <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-4">No certificates on file.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {q.certificates.filter(c => c.is_active).map(cert => (
                    <CertCard key={cert.id} cert={cert} partyId={vendorId} />
                  ))}
                </div>
              )}
            </div>

            {/* Licenses */}
            <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Licenses</h3>
                <button type="button" onClick={() => setAddLicOpen(true)}
                  className="text-xs px-2.5 py-1 rounded border border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-950">
                  + Add license
                </button>
              </div>
              {q.licenses.filter(l => l.is_active).length === 0 ? (
                <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-4">No licenses on file.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-gray-800">
                      <th className="text-left py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Type</th>
                      <th className="text-left py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Number</th>
                      <th className="text-left py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Jurisdiction</th>
                      <th className="text-left py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Expiry</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50 dark:divide-gray-800">
                    {q.licenses.filter(l => l.is_active).map((lic: VendorLicense) => (
                      <tr key={lic.id}>
                        <td className="py-2 text-slate-700 dark:text-slate-300 capitalize">{lic.license_type}</td>
                        <td className="py-2 font-mono text-xs text-slate-600 dark:text-slate-300">{lic.license_number}</td>
                        <td className="py-2 text-slate-500 dark:text-slate-400">{lic.issuing_jurisdiction}</td>
                        <td className="py-2"><ExpiryBadge dateStr={lic.expiry_date} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Bonds */}
            {q.bonds.length > 0 && (
              <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Bonds</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-gray-800">
                      <th className="text-left py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Type</th>
                      <th className="text-left py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Surety</th>
                      <th className="text-right py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Amount</th>
                      <th className="text-left py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Expiry</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50 dark:divide-gray-800">
                    {q.bonds.map((b: VendorBond) => (
                      <tr key={b.id}>
                        <td className="py-2 capitalize text-slate-700 dark:text-slate-300">{b.bond_type}</td>
                        <td className="py-2 text-slate-600 dark:text-slate-300">{b.surety_name}</td>
                        <td className="py-2 text-right font-mono text-slate-700 dark:text-slate-300">{fmtCurrency(b.bond_amount)}</td>
                        <td className="py-2">{b.expiry_date ? <ExpiryBadge dateStr={b.expiry_date} /> : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Documents */}
            <VendorDocumentsSection partyId={vendorId} qualId={q.id} />

            {/* Audit timeline */}
            <AuditTimeline partyId={vendorId} />
          </div>

          {/* Right column: editable info cards */}
          <div className="space-y-4">
            {/* W-9 + Tax */}
            <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Tax / W-9</h3>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => setEditTaxOpen(true)} sx={{ color: "#94a3b8" }}>
                    <EditIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </div>
              <dl className="space-y-2">
                <InfoRow label="W-9 on file" value={q.w9_on_file ? <span className="text-emerald-600 dark:text-emerald-400 font-medium">Yes</span> : <span className="text-amber-600 dark:text-amber-400">No</span>} />
                {q.w9_signed_date && <InfoRow label="W-9 signed" value={fmtDate(q.w9_signed_date)} />}
                <InfoRow label="EIN" value={q.ein ?? "—"} />
                <InfoRow label="Business type" value={q.business_type ?? "—"} />
                <InfoRow label="1099 eligible" value={q.is_1099_eligible ? "Yes" : "No"} />
                {q.payment_terms_default && <InfoRow label="Payment terms" value={q.payment_terms_default} />}
              </dl>
            </div>

            {/* Safety */}
            <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Safety</h3>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => setEditSafetyOpen(true)} sx={{ color: "#94a3b8" }}>
                    <EditIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </div>
              <dl className="space-y-2">
                <InfoRow label="EMR" value={q.emr != null ? `${q.emr}${q.emr_year ? ` (${q.emr_year})` : ""}` : "—"} />
                <InfoRow label="TRIR" value={q.trir != null ? `${q.trir}${q.trir_year ? ` (${q.trir_year})` : ""}` : "—"} />
                <InfoRow label="OSHA 300 on file" value={q.osha_300_on_file ? "Yes" : "No"} />
                <InfoRow label="Drug testing policy" value={q.drug_testing_policy ? "Yes" : "No"} />
                {q.union_local && <InfoRow label="Union local" value={q.union_local} />}
                {q.union_craft && <InfoRow label="Union craft" value={q.union_craft} />}
                {q.prevailing_wage_compliant && <InfoRow label="Prevailing wage" value="Compliant" />}
              </dl>
            </div>

            {/* Diversity */}
            <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Diversity Certifications</h3>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => setEditDiversityOpen(true)} sx={{ color: "#94a3b8" }}>
                    <EditIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </div>
              <div className="flex flex-wrap gap-2">
                {q.is_mbe && <DiversityBadge label="MBE" expiry={q.mbe_cert_expiry} />}
                {q.is_wbe && <DiversityBadge label="WBE" expiry={q.wbe_cert_expiry} />}
                {q.is_dbe && <DiversityBadge label="DBE" expiry={q.dbe_cert_expiry} />}
                {q.is_vosb && <DiversityBadge label="VOSB" expiry={null} />}
                {!q.is_mbe && !q.is_wbe && !q.is_dbe && !q.is_vosb && (
                  <span className="text-sm text-slate-400 dark:text-slate-500">None on file</span>
                )}
              </div>
            </div>

            {/* Financial */}
            <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Financial</h3>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => setEditFinancialOpen(true)} sx={{ color: "#94a3b8" }}>
                    <EditIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </div>
              <dl className="space-y-2">
                <InfoRow label="Credit limit" value={fmtCurrency(q.credit_limit)} />
                <InfoRow label="Bond capacity (single)" value={fmtCurrency(q.bond_capacity_single)} />
                <InfoRow label="Bond capacity (aggregate)" value={fmtCurrency(q.bond_capacity_aggregate)} />
                {q.credit_score != null && (
                  <InfoRow label="Credit score" value={`${q.credit_score}${q.credit_score_source ? ` (${q.credit_score_source})` : ""}`} />
                )}
              </dl>
            </div>

            {/* Conditional warning */}
            {q.qual_status === "conditional" && q.conditional_until && (
              <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex gap-2">
                <WarningAmberIcon sx={{ fontSize: 16, color: "#d97706", flexShrink: 0, mt: 0.2 }} />
                <div className="text-xs text-amber-800 dark:text-amber-200">
                  Conditional approval until {fmtDate(q.conditional_until)}.
                  {q.conditional_max_po_value != null && (
                    <> Max PO value: {fmtCurrency(q.conditional_max_po_value)}.</>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dialogs */}
      <AddCertDialog open={addCertOpen} qualId={q?.id ?? ""} partyId={vendorId} onClose={() => setAddCertOpen(false)} />
      <AddLicenseDialog open={addLicOpen} partyId={vendorId} onClose={() => setAddLicOpen(false)} />
      <AiUploadDialog open={aiUploadOpen} partyId={vendorId} onClose={() => setAiUploadOpen(false)} onDone={() => { qc.invalidateQueries({ queryKey: ["vendor-qual", vendorId] }); }} />

      {q && (
        <>
          <EditTaxDialog
            open={editTaxOpen}
            partyId={vendorId}
            initial={{
              ein: q.ein ?? "",
              business_type: q.business_type ?? "",
              is_1099_eligible: q.is_1099_eligible,
              w9_on_file: q.w9_on_file,
              w9_signed_date: q.w9_signed_date ?? "",
              payment_terms_default: q.payment_terms_default ?? "",
            }}
            onClose={() => setEditTaxOpen(false)}
          />
          <EditSafetyDialog
            open={editSafetyOpen}
            partyId={vendorId}
            initial={{
              emr: q.emr != null ? String(q.emr) : "",
              emr_year: q.emr_year != null ? String(q.emr_year) : "",
              trir: q.trir != null ? String(q.trir) : "",
              trir_year: q.trir_year != null ? String(q.trir_year) : "",
              osha_300_on_file: q.osha_300_on_file,
              drug_testing_policy: q.drug_testing_policy,
              union_local: q.union_local ?? "",
              union_craft: q.union_craft ?? "",
              prevailing_wage_compliant: q.prevailing_wage_compliant,
            }}
            onClose={() => setEditSafetyOpen(false)}
          />
          <EditDiversityDialog
            open={editDiversityOpen}
            partyId={vendorId}
            initial={{
              is_mbe: q.is_mbe,
              mbe_cert_expiry: q.mbe_cert_expiry ?? "",
              is_wbe: q.is_wbe,
              wbe_cert_expiry: q.wbe_cert_expiry ?? "",
              is_dbe: q.is_dbe,
              dbe_cert_expiry: q.dbe_cert_expiry ?? "",
              is_vosb: q.is_vosb,
            }}
            onClose={() => setEditDiversityOpen(false)}
          />
          <EditFinancialDialog
            open={editFinancialOpen}
            partyId={vendorId}
            initial={{
              credit_limit: q.credit_limit != null ? String(q.credit_limit) : "",
              bond_capacity_single: q.bond_capacity_single != null ? String(q.bond_capacity_single) : "",
              bond_capacity_aggregate: q.bond_capacity_aggregate != null ? String(q.bond_capacity_aggregate) : "",
            }}
            onClose={() => setEditFinancialOpen(false)}
          />
        </>
      )}
    </div>
  );
}

// ─── micro components ─────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center gap-2">
      <dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="text-xs text-slate-700 dark:text-slate-300 text-right">{value}</dd>
    </div>
  );
}

function DiversityBadge({ label, expiry }: { label: string; expiry: string | null }) {
  const content = (
    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 dark:bg-violet-950 text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-violet-800">
      {label}
    </span>
  );
  if (expiry) return <Tooltip title={`Expires ${fmtDate(expiry)}`}>{content}</Tooltip>;
  return content;
}

function AuditTimeline({ partyId }: { partyId: string }) {
  const eventsQ = useQuery({
    queryKey: ["vendor-qual-events", partyId],
    queryFn: () => api<Array<{
      id: string; event_type: string; from_status: string | null;
      to_status: string | null; reason: string | null; actor_is_system: boolean; created_at: string;
    }>>(`/api/vendors/${partyId}/qualification/events`),
    staleTime: 30_000,
  });

  if (eventsQ.isLoading) return <div className="text-xs text-slate-400 dark:text-slate-500 py-2">Loading events…</div>;
  if (!eventsQ.data?.length) return null;

  return (
    <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Audit Timeline</h3>
      <ol className="relative border-l border-slate-200 dark:border-gray-700 space-y-3 ml-2">
        {eventsQ.data.map(ev => (
          <li key={ev.id} className="ml-4">
            <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border-2 border-white dark:border-gray-900 bg-slate-300 dark:bg-gray-600" />
            <div className="text-xs">
              <span className="font-medium text-slate-700 dark:text-slate-300 capitalize">{ev.event_type.replace(/_/g, " ")}</span>
              {ev.from_status && ev.to_status && (
                <span className="text-slate-400 dark:text-slate-500 ml-1">{ev.from_status} → {ev.to_status}</span>
              )}
              <span className="text-slate-400 dark:text-slate-500 ml-2">{fmtDate(ev.created_at)}</span>
              {ev.actor_is_system && <span className="ml-1 text-slate-400 dark:text-slate-500 italic">· system</span>}
            </div>
            {ev.reason && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{ev.reason}</p>}
          </li>
        ))}
      </ol>
    </div>
  );
}
